/**
 * Page settings — the page's name, and the fields that become Webflow's SEO panel.
 *
 * The title had no editing affordance at all: it rendered in the header and nothing could
 * change it. It lives here rather than as a lone rename box because the slug and meta fields
 * need the same home, and because they check against each other — the keyword wants to be in
 * the title, the slug and the description.
 */

import { useRef, useState } from 'react';
import { useDocStore } from '../store/docStore.js';
import { api } from '../api/client.js';
import type { CollctivDoc } from '@lp/shared';
import {
  checkMetaDescription,
  checkMetaTitle,
  checkSlug,
  measureTitlePx,
  slugify,
  suggestMetaTitle,
  DESC_CHAR_LIMIT,
  TITLE_CHAR_LIMIT,
  TITLE_PX_LIMIT,
  type FieldCheck,
} from '../seo/metrics.js';

function Check({ check }: { check: FieldCheck | null }): React.ReactElement | null {
  if (!check) return null;
  return <span className={`lps__check lps__check--${check.status}`}>{check.message}</span>;
}

function Counter({ value, limit }: { value: number; limit: number }): React.ReactElement {
  return (
    <span className={`lps__count${value > limit ? ' lps__count--over' : ''}`}>
      {value}/{limit}
    </span>
  );
}

/** The save indicator, so "is my work safe?" is answerable at a glance. */
export function SaveStatus(): React.ReactElement {
  const saveState = useDocStore((s) => s.saveState);
  const lastSavedAt = useDocStore((s) => s.lastSavedAt);
  const saveError = useDocStore((s) => s.saveError);
  const saveNow = useDocStore((s) => s.saveNow);

  const label: Record<string, string> = {
    idle: 'Not saved yet',
    dirty: 'Unsaved changes',
    saving: 'Saving…',
    saved: lastSavedAt
      ? `Saved ${new Date(lastSavedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
      : 'Saved',
    offline: 'Offline — kept in this browser',
    error: saveError ? `Save failed: ${saveError}` : 'Save failed',
  };

  const retryable = saveState === 'error' || saveState === 'dirty';

  return (
    <button
      type="button"
      className={`lp-save lp-save--${saveState}`}
      onClick={() => retryable && void saveNow()}
      title={retryable ? 'Save now' : label[saveState]}
      disabled={!retryable}
    >
      <span className="lp-save__dot" aria-hidden />
      {label[saveState]}
    </button>
  );
}

export function PageSettingsPanel(): React.ReactElement {
  const title = useDocStore((s) => s.title);
  const meta = useDocStore((s) => s.meta);
  const setTitle = useDocStore((s) => s.setTitle);
  const setMeta = useDocStore((s) => s.setMeta);
  const docId = useDocStore((s) => s.id);
  const saveNow = useDocStore((s) => s.saveNow);
  const replaceDoc = useDocStore((s) => s.replaceDoc);
  const [open, setOpen] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  /** Export goes through the server so the filename comes from the slug. */
  async function exportDoc(): Promise<void> {
    await saveNow();
    window.location.href = api.exportUrl(docId);
  }

  async function importDoc(file: File): Promise<void> {
    setImportError(null);
    try {
      const parsed = JSON.parse(await file.text()) as CollctivDoc;
      if (!parsed || typeof parsed !== 'object' || !parsed.puck) {
        throw new Error('That does not look like a page file.');
      }
      // A fresh id, so importing never overwrites the page it came from.
      replaceDoc({ ...parsed, id: `doc-${Date.now().toString(36)}` });
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'Could not read that file.');
    }
  }

  const kw = meta.primaryKeyword;
  const titlePx = measureTitlePx(meta.metaTitle);

  if (!open) {
    return (
      <button
        type="button"
        className="lp-btn"
        onClick={() => setOpen(true)}
        title="Page name, slug and search listing"
      >
        Page settings
      </button>
    );
  }

  return (
    <div className="lp-export lps">
      <div className="lp-export__head">
        <strong>Page settings</strong>
        <button type="button" className="lp-btn lp-btn--ghost" onClick={() => setOpen(false)}>
          Close
        </button>
      </div>

      <label className="lps__row">
        <span className="lps__label">Page name</span>
        <input
          className="lpf__input"
          value={title}
          placeholder="Leaving gift collections"
          onChange={(e) => setTitle(e.target.value)}
        />
        <span className="lps__hint">Just for you — it never appears on the page.</span>
      </label>

      <label className="lps__row">
        <span className="lps__label">Primary keyword</span>
        <input
          className="lpf__input"
          value={kw}
          placeholder="leaving gift collection"
          onChange={(e) => setMeta({ primaryKeyword: e.target.value })}
        />
        <span className="lps__hint">One primary keyword. Everything below is checked against it.</span>
      </label>

      <label className="lps__row">
        <span className="lps__label">
          URL slug
          <button
            type="button"
            className="lps__suggest"
            onClick={() => setMeta({ slug: slugify(kw || title) })}
          >
            Suggest
          </button>
        </span>
        <input
          className="lpf__input"
          value={meta.slug}
          placeholder="leaving-gift-collection"
          onChange={(e) => setMeta({ slug: e.target.value })}
        />
        <Check check={checkSlug(meta.slug, kw)} />
      </label>

      <label className="lps__row">
        <span className="lps__label">
          Meta title
          <button
            type="button"
            className="lps__suggest"
            onClick={() => setMeta({ metaTitle: suggestMetaTitle(title) })}
          >
            Suggest
          </button>
          <Counter value={meta.metaTitle.length} limit={TITLE_CHAR_LIMIT} />
        </span>
        <input
          className="lpf__input"
          value={meta.metaTitle}
          placeholder="Leaving Gift Collections — Sorted | Collctiv"
          onChange={(e) => setMeta({ metaTitle: e.target.value })}
        />
        {/*
          Google truncates by pixel width, not characters, so 55 characters of "WILLIAM
          WILLIAMSON" and 55 of "iiii iiii" behave completely differently. Both are shown.
        */}
        <span className="lps__bar" aria-hidden>
          <span
            className={`lps__bar-fill${titlePx > TITLE_PX_LIMIT ? ' lps__bar-fill--over' : ''}`}
            style={{ width: `${Math.min(100, (titlePx / TITLE_PX_LIMIT) * 100)}%` }}
          />
        </span>
        <span className="lps__hint">{titlePx}px of ~{TITLE_PX_LIMIT}px before Google cuts it off</span>
        <Check check={checkMetaTitle(meta.metaTitle, kw)} />
      </label>

      <label className="lps__row">
        <span className="lps__label">
          Meta description
          <Counter value={meta.metaDescription.length} limit={DESC_CHAR_LIMIT} />
        </span>
        <textarea
          className="lpf__input lps__textarea"
          value={meta.metaDescription}
          rows={3}
          placeholder="Collecting for a leaving gift? Here's the easiest way to gather the money — no chasing, no spreadsheet."
          onChange={(e) => setMeta({ metaDescription: e.target.value })}
        />
        <Check check={checkMetaDescription(meta.metaDescription, kw)} />
      </label>

      <p className="lp-export__hint">
        These go into Webflow’s own SEO fields — the builder doesn’t carry them across.
      </p>

      <div className="lps__file">
        <button type="button" className="lp-btn" onClick={() => void exportDoc()}>
          Export page
        </button>
        <button type="button" className="lp-btn" onClick={() => fileInput.current?.click()}>
          Import page
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="application/json,.json"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void importDoc(f);
            e.target.value = '';
          }}
        />
      </div>
      {importError && <span className="lpf__error">{importError}</span>}
      <p className="lp-export__hint">
        A page file is plain JSON — back it up, or hand it to someone else to open.
      </p>
    </div>
  );
}
